<template>
  <section class="create-document-modal-upload">
    <div v-if="hasQesAccess && !uploadedFilesLength && !hasQesFiles" class="toggle-container">
      <FriedSwitch
        v-model="isQesFile"
        class="qes-switch"
        :disabled="disableToggleQesFile"
        :label="t('qes_files_switch_label')"
        data-external="create-document-qes-file-switch"
      />
      <FriedInfoPopper>
        <FriedParagraph>
          {{ t('qes_file_upload_description', { fileTypes: qesFileType.toString() }) }}
        </FriedParagraph>
      </FriedInfoPopper>
    </div>
    <CreateDocumentUploadQes
      v-if="isQesFile || hasQesFiles"
      :document-id="documentId"
      :qes-files="qesFiles"
      @reload-qes-files="$emit('reload-qes-files')"
    />
    <template v-else>
      <UploadButton
        v-if="isXXSmallScreen"
        :accepted-file-types="allAcceptedFileTypes.toString()"
        multiple
        @select-file="handleChange"
      >
        <FriedRoundedButton
          class="upload-button"
          icon-align-right
          data-external="create-document-upload-button"
        >
          {{ $t('browse-to-choose-a-file') }}
          <template #icon>
            <FriedIcon icon="upload" />
          </template>
        </FriedRoundedButton>
      </UploadButton>
      <FriedDropzone
        v-else
        class="dropzone"
        :accepted-file-types="allAcceptedFileTypes.toString()"
        multiple
        @update:model-value="handleChange"
        @error="handleError"
      >
        <template v-if="!!errorMessage.title" #title>
          {{ errorMessage.title }}
        </template>
        <template v-else #title>
          {{ dropzoneLocalization().drag_and_drop_or }}
          {{ dropzoneLocalization().browse_to_choose_a_file }}
        </template>
        <template #description> {{ `${dropzoneLocalization().max_file_size}: 20MB` }}</template>
      </FriedDropzone>
      <section v-if="!!uploadedFilesLength" class="file-list-container">
        <FriedH5 class="file-header">{{ t('files') }}</FriedH5>
        <FriedListItem
          v-if="uploadedFilesLength === 1"
          :key="uploadedFiles[0].name"
          :title="uploadedFiles[0].name"
          :show-description="false"
          class="file-list-item"
        >
          <template #left>
            <FriedIcon icon="file-blank" />
          </template>
          <template #right>
            <FriedIconButton
              data-external="file-delete-button"
              @click="handleDelete(uploadedFiles[0].name)"
            >
              <FriedIcon icon="delete-bin" />
            </FriedIconButton>
            <FriedIcon v-if="uploadedFilesLength > 1" class="drag-handle" icon="drag-handle" />
          </template>
        </FriedListItem>
        <draggable
          v-else-if="uploadedFilesLength > 1"
          :model-value="uploadedFiles"
          tag="ul"
          class="file-list"
          item-key="name"
          @end="reorganizeFileOrder"
        >
          <template #item="{ element }">
            <FriedListItem
              :key="element.name"
              class="file-list-item draggable"
              :title="element.name"
              :show-description="false"
            >
              <template #left>
                <FriedIcon icon="file-blank" />
              </template>
              <template #right>
                <FriedIconButton
                  v-tooltip="{
                    content: t('delete'),
                    delay: { show: 1000, hide: 100 },
                    distance: 8,
                  }"
                  data-external="file-delete-button"
                  @click="handleDelete(element.name)"
                >
                  <FriedIcon icon="delete-bin" />
                </FriedIconButton>
                <FriedIcon v-if="uploadedFilesLength > 1" class="drag-handle" icon="drag-handle" />
              </template>
            </FriedListItem>
          </template>
        </draggable>
      </section>
    </template>
  </section>
</template>

<script lang="ts" setup>
import { t } from '@getaccept/lib-shared/src/helpers/translation.helper';
import { computed, ref } from 'vue';
import {
  allAcceptedFileTypes,
  qesFileType,
} from '@getaccept/lib-shared/src/file/constants/accepted-file-types';
import Translations from '@getaccept/lib-shared/src/constants/translations.constants';
import { Feature } from '@getaccept/feature-restriction';
import { FileHelper } from '@getaccept/lib-shared/src/file/helpers/file.helper';
import draggable from 'vuedraggable';
import type { DocumentQesFile } from '@getaccept/lib-shared/src/qes/types/document-qes-file';
import useBreakpoints from '@getaccept/lib-shared/src/composables/breakpoints.composable';
import { useEntitySettings } from '../../entity/composables/entity-settings.composable';
import UploadButton from '../../components/UploadButton.vue';
import CreateDocumentUploadQes from './CreateDocumentUploadQes.vue';

const {
  documentId,
  uploadedFiles,
  qesFiles = [],
} = defineProps<{
  documentId: string;
  uploadedFiles: File[];
  qesFiles?: DocumentQesFile[];
}>();
const emit = defineEmits<{
  'update-files': [File[]];
  'reload-qes-files': [];
}>();

const { isXXSmallScreen } = useBreakpoints();

const hasQesAccess = computed(() => useEntitySettings().hasAccess(Feature.QES));

const hasQesFiles = computed(() => qesFiles?.length > 0);
const uploadedFilesLength = computed(() => uploadedFiles?.length);

const isQesFile = ref(false);

const disableToggleQesFile = computed(
  () =>
    uploadedFilesLength.value > 1 ||
    (uploadedFilesLength.value === 1 &&
      !qesFileType.includes(FileHelper.extractFileType(uploadedFiles[0].name)))
);

const errorMessage = ref({ title: '', description: '' });

const handleError = (fileNames: string[]) => {
  if (fileNames.length) {
    errorMessage.value = {
      title: t('unsupported-format-title'),
      description: t('unsupported-format-description'),
    };
  } else {
    errorMessage.value = { title: '', description: '' };
  }
};

const sizeLimit = 20000000;
const maxFiles = 5;

const handleChange = (fileList: File[]) => {
  if (!fileList) return;

  const files: File[] = Array.from(fileList);

  if (files.length > maxFiles) {
    errorMessage.value = {
      title: t('too-many-files-title'),
      description: t('too-many-files-description', { 'max-files': maxFiles }),
    };
    return;
  }

  const totalSize = files.reduce((acc, { size }) => acc + size, 0);

  if (totalSize > sizeLimit) {
    errorMessage.value = {
      title: t('too-large-files-title'),
      description: t('too-large-files-description', { 'size-limit': sizeLimit / 1000000 }),
    };
    return;
  }

  const hasDocxFile = files.some(({ name }) => FileHelper.extractFileType(name) === '.docx');

  if (hasDocxFile && files.length > 1) {
    errorMessage.value = {
      title: t('one-word-file-at-a-time-title'),
      description: t('one-word-file-at-a-time-description'),
    };
    return;
  }
  emit('update-files', [...uploadedFiles, ...files]);
};

const handleDelete = (name: string) => {
  emit(
    'update-files',
    uploadedFiles.filter(file => file.name !== name)
  );
};

const reorganizeFileOrder = ({ newIndex, oldIndex }: { newIndex: number; oldIndex: number }) => {
  const files = [...uploadedFiles];
  const [removed] = files.splice(oldIndex, 1);
  files.splice(newIndex, 0, removed);
  emit('update-files', files);
};

const { dropzoneLocalization } = Translations;
</script>

<style lang="scss" scoped>
.create-document-modal-upload {
  grid-area: content;

  .toggle-container {
    display: flex;
    gap: var(--spacing-50);
  }

  .dropzone {
    flex: 0 1 fit-content;
  }

  .file-header {
    padding: var(--spacing-50) 0;
    text-transform: capitalize;
  }

  .file-list {
    overflow: auto;
    width: 100%;
    height: 100%;
    padding: 0;
    clear: both;
    margin: 0;
    display: flex;
    flex-direction: column;
    gap: var(--spacing-100);

    &-item {
      box-shadow: var(--shadow-1);
      padding: var(--spacing-50) var(--spacing-100);
      border-radius: var(--radius-medium);
      margin: 0 2px;
      display: grid;
      grid-template-columns: 1fr auto auto;

      &.draggable {
        cursor: move;
      }
    }
  }
}
</style>
